import { useNavigate, useParams } from "react-router-dom";
import useModule from "../../hooks/useModule";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useQueriedRecords from "../../api/api-hooks/records/useQueriedRecords";
import { objectFilter, selectableOpen } from "../../atoms/GlobalAtoms";
import { useAtomValue } from "jotai";
import useToggle from "../../hooks/useToggle";
import PopoverDropdownMenu from "../../components/Menus/PopoverDropdownMenu";
import DropdownMenuDivider from "../../components/Menus/DropdownMenuDivider";
import DropdownOptionWithIcon from "../../components/Menus/DropdownOptionWithIcon";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import { Button, ButtonGroup, Col, Container, Row } from "reactstrap";
import { Popover, Whisper } from "rsuite";
import FiltersButton from "../../components/SavedFilters/FiltersButton";
import SortBy from "../../components/PageView/PageViewTable/SortBy";
import TableActionsMenu from "../../components/CRM/TableActionsMenu";
import AppTable from "../../components/AppTable/AppTable";
import DownloadTemplateModal from "../../components/Modals/DownloadTemplateModal";
import UpdateRecordsDrawer from "../../components/Drawers/Records/UpdateRecordsDrawer";
import BulkApplyTagsDrawer from "../../components/Drawers/Records/BulkApplyTagsDrawer";
import ImportCSVDrawer from "../../components/Drawers/ImportCSVDrawer";
import CreateRecordDrawer from "../../components/Drawers/Records/CreateRecordDrawer";
import DataExportDrawer from "../../components/Drawers/DataExportDrawer";

const initialSelected = {};

const CRMRecordListView = () => {
  const navigate = useNavigate();
  const { tenantID, ModuleAPIName: moduleAPIName } = useParams();
  const { module, modulesLoading } = useModule(moduleAPIName);
  const [selected, setSelected] = useState(initialSelected);
  const [query, setQuery] = useState({});
  const { records, pagination, recordsLoading, error, errorMsg } =
    useQueriedRecords({
      moduleAPIName: moduleAPIName,
      query: query,
    });

  const view = useAtomValue(objectFilter);
  const sOpen = useAtomValue(selectableOpen);

  const [importCSVDrawer, toggleImportCSVDrawer] = useToggle();
  const [updateRecordsDrawer, toggleUpdateRecordsDrawer] = useToggle();
  const [createRecordsDrawer, toggleCreateRecordsDrawer] = useToggle();
  const [templateModal, toggleTemplateModal] = useToggle();
  const [bulkApplyTags, toggleBulkApplyTags] = useToggle();
  const [exportDrawer, toggleExportDrawer] = useToggle();

  const selectedIDs = useMemo(() => {
    const keys = Object.keys(selected);
    let newSelectedRecords = [];
    keys.forEach((key) => {
      newSelectedRecords.push(records[key].id || records[key]._id);
    });
    return newSelectedRecords;
  }, [selected, records]);

  const actionsRef = useRef();
  const filterDropdownRef = useRef();
  const tableSettingsRef = useRef();
  const newWhisperRef = useRef();

  const loadModule = useCallback(() => {
    if (modulesLoading) return;

    const fields = getQueryFields(module);

    let query = {
      Fields: fields,
      OrderBy: module?.Templates?.RecordListView?.OrderBy ?? {
        Field: "Created",
        Direction: "ASC",
      },
      Filters: [],
      Pagination: {
        Count: 25,
        Page: 1,
      },
    };

    setQuery(query);
    setSelected(initialSelected);
  }, [modulesLoading, module, tenantID, navigate]);

  const getQueryFields = (module) => {
    if (!module) {
      return;
    }

    let queryFields = [
      "id",
      "_RecordName",
      ...(module?.Templates?.RecordListView?.Fields ?? []),
    ];

    return queryFields || ["*"];
  };

  const clearFilters = () => {
    setQuery((prev) => ({ ...prev, Filters: [] }));
  };

  const updateSortBy = (sortBy) => {
    let q = query;

    q.OrderBy = sortBy ? sortBy : null;

    if (q.OrderBy === null) {
      delete q.OrderBy;
    }

    q = structuredClone(q);

    setQuery(q);
  };

  useEffect(() => {
    loadModule();
  }, [loadModule]);

  const NewRecordOptions = () => (
    <PopoverDropdownMenu>
      <h5 className="px-2 pt-2 text-dark">Quick Create </h5>
      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        name="Import From CSV"
        onClick={() => {
          toggleImportCSVDrawer();
          newWhisperRef.current.close();
        }}
        icon="mdi mdi-upload"
      />
    </PopoverDropdownMenu>
  );

  const SettingsOptions = () => (
    <PopoverDropdownMenu>
      <h5 className="px-2 pt-2 text-dark">Manage {module?.Name}</h5>
      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        name="Settings"
        onClick={() => {
          navigate(
            `/clouds/${tenantID}/settings/modules/manage/${
              module?.APIName ?? ""
            }/fields`
          );
        }}
        icon="mdi mdi-cog"
      />
    </PopoverDropdownMenu>
  );

  const selectedRecordKeys = Object.keys(selected);
  const selectedRecordsLength = selectedRecordKeys.length;
  const isSelection = selectedRecordsLength > 0;

  const selectedRecords = [];

  if (isSelection && records?.length) {
    for (const key of selectedRecordKeys) {
      selectedRecords.push(records[key]);
    }
  }

  return (
    <>
      <AppHelmet title={`${module?.Name || "Loading..."} - GrowthCloud.com`} />
      <div className="header pb-6">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-2">
              <Col xs="auto">
                <h2 className="m-0">
                  {modulesLoading ? (
                    <div
                      className="skeleton"
                      style={{ width: 200, height: 20 }}
                    ></div>
                  ) : (
                    module?.Name
                  )}
                </h2>
              </Col>
              <Col align="right">
                <Whisper
                  placement="autoVerticalEnd"
                  trigger="click"
                  ref={tableSettingsRef}
                  speaker={
                    <Popover
                      className="sidebar-popover"
                      full
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <SettingsOptions />
                    </Popover>
                  }
                  controlId="newRecordDropdown"
                >
                  <Button
                    size="sm"
                    color="secondary"
                    outline
                    className="btn-icon-only"
                  >
                    <i className="mdi mdi-dots-horizontal"></i>
                  </Button>
                </Whisper>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      <Container className="mt--6 px-4" fluid>
        <hr className="mt-0 mb-3" />

        {modulesLoading ? null : (
          <>
            <div className="d-flex align-items-center mb-3">
              <ButtonGroup className="mr-2">
                <Button
                  size="sm"
                  color="primary"
                  className="px-2"
                  onClick={() => {
                    toggleCreateRecordsDrawer();
                  }}
                >
                  New
                </Button>
                <Whisper
                  placement="autoVerticalStart"
                  trigger="click"
                  ref={newWhisperRef}
                  speaker={
                    <Popover
                      className="sidebar-popover"
                      full
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <NewRecordOptions />
                    </Popover>
                  }
                  controlId="newRecordDropdown"
                >
                  <Button size="sm" color="primary" className="btn-icon-only">
                    <i className="mdi mdi-chevron-down"></i>
                  </Button>
                </Whisper>
              </ButtonGroup>
              <span className="mr-2">
                <FiltersButton
                  currentModule={module}
                  query={query}
                  setQuery={setQuery}
                  saveAs={false}
                  onApply={(out, close = true) => {
                    const newQuery = _.clone(query);

                    newQuery.Filters = out?.Filters;

                    setQuery(newQuery);

                    if (close) {
                      filterDropdownRef?.current?.close();
                    }
                  }}
                  savedQuery={query}
                  onClear={clearFilters}
                />
              </span>
              <SortBy
                sortBy={query?.OrderBy}
                updateSortBy={updateSortBy}
                moduleAPIName={moduleAPIName}
                query={query}
              />

              {isSelection && (
                <Col align="right">
                  <Whisper
                    placement="autoVerticalEnd"
                    trigger="click"
                    ref={actionsRef}
                    speaker={
                      <Popover
                        className="sidebar-popover"
                        full
                        onClick={() => actionsRef.current.close()}
                      >
                        <TableActionsMenu
                          showImportOption={false}
                          moduleAPIName={moduleAPIName}
                          isSelection={selectedRecords.length > 0}
                          selectedRecords={selectedRecords}
                          setSelectedRecords={setSelected}
                          load={loadModule}
                          toggleUpdate={toggleUpdateRecordsDrawer}
                          toggleUpdateTags={toggleBulkApplyTags}
                          toggleTemplateCSV={toggleTemplateModal}
                          setExportDrawer={toggleExportDrawer}
                        />
                      </Popover>
                    }
                  >
                    <Button
                      size="sm"
                      color="primary"
                      className="d-flex align-items-center"
                    >
                      {selectedRecords?.length}&nbsp;Selected
                      <i
                        className="mdi mdi-chevron-down ml-2 mr--1"
                        style={{
                          fontSize: 22,
                        }}
                      ></i>
                    </Button>
                  </Whisper>
                </Col>
              )}
            </div>
            <AppTable
              query={query}
              setQuery={setQuery}
              moduleAPIName={moduleAPIName}
              selectableRows={true}
              onSort={(sortBy) => updateSortBy(sortBy)}
              columnAdd={false}
              selectedRecords={selected}
              setSelectedRecords={setSelected}
              data={records}
              pagination={pagination}
              loading={recordsLoading}
              error={error}
              errorMsg={errorMsg}
              sticky
              containerClasses={"pr-4"}
              paginationCSS="pr-6"
            />
          </>
        )}
      </Container>
      <DownloadTemplateModal
        open={templateModal}
        handleClose={toggleTemplateModal}
        moduleAPIName={module?.APIName}
      />
      <UpdateRecordsDrawer
        open={updateRecordsDrawer}
        onClose={toggleUpdateRecordsDrawer}
        records={selectedRecords}
        moduleAPIName={module?.APIName}
      />
      <BulkApplyTagsDrawer
        open={bulkApplyTags}
        onClose={toggleBulkApplyTags}
        records={selectedRecords}
        moduleAPIName={module?.APIName}
      />
      <ImportCSVDrawer
        open={importCSVDrawer}
        onClose={toggleImportCSVDrawer}
        onCreate={toggleImportCSVDrawer}
        moduleAPIName={module?.APIName}
      />
      <CreateRecordDrawer
        open={createRecordsDrawer}
        onClose={toggleCreateRecordsDrawer}
        onCreate={() => {
          toggleCreateRecordsDrawer();
        }}
        moduleAPIName={module?.APIName}
      />
      <DataExportDrawer
        open={exportDrawer}
        moduleAPIName={module?.APIName}
        recordIDs={selectedIDs}
        onClose={toggleExportDrawer}
      />
    </>
  );
};

export default CRMRecordListView;
